////////LIBRARY/////////
import { cloneDeep, map, isEmpty, isUndefined } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { shallowEqual, useSelector } from 'react-redux'
import styled from 'styled-components'
///////COMPONENTS///////
import UseWindowResize from '../../../../hooks/UseWindowResize'
import { TableWrapper, TableContent } from '../../../../theme/GlobalCss'
import UseColumnsManager from '../../hooks/UseColumnsManager'
import UseTableSort from '../../hooks/UseTableSort'
import TableNestedBody from '../TableNestedBody'
import TableNestedHeader from '../TableNestedHeader'

/////////STYLED/////////
const TableReverse = styled.form`
	overflow-x: ${(props) => (props.isScrollable ? 'scroll' : 'hidden')};
	height: ${(props) => (props.autosize ? 'auto' : `${props.sizeheight - 345}px`)};
	&::-webkit-scrollbar {
		width: 50px;
		background-color: rgba(0, 0, 0, 0.15);
		height: 15px;
	}
	&::-webkit-scrollbar-thumb {
		background: #4d4f5c;
	}
	/* scrollbar styling standard version */
	& {
		scrollbar-color: #4d4f5c #e5e5e5;
		scrollbar-width: thin;
	}
	&::-webkit-scrollbar-button {
		display: none;
	}
`
const ScrollTopWrapper = styled.div`
	width: ${(props) => props.width};
	/*overflow-x: ${(props) => (props.isScrollable ? 'scroll' : 'hidden')};*/
`
const ScrollTop = styled.div`
	width: ${(props) => props.width};
	height: 1px;
`
/////////STYLED/////////
/**
 *
 * @param {array} inputColumns
 * @param {array} inputData
 * @param {object} inputConfig
 * @return {*}
 * @constructor
 */
const TableNested = ({ inputColumns, inputData, inputConfig }) => {
	const tableRef = useRef()
	const tableRefWrapper = useRef()
	const tableRefTop = useRef()
	const bodyWrapperRef = useRef() // Equivalent to size of rows

	useSelector(
		(state) => ({
			user: state.user.identity.rolePrivileges.privileges,
		}),
		shallowEqual
	)

	const { handleSubmit } = useForm({
		mode: 'onChange',
	})

	const [editing, setEditing] = useState(false)
	const [config, setConfig] = useState(inputConfig)
	const selectedData = useState([])
	const [columns] = UseColumnsManager(inputColumns)
	const [dimensions] = UseWindowResize()

	const [tableData, trigger, setTrigger, setTableData, setForceUnsort] = UseTableSort(inputColumns)

	///////////////////////////////// HANDLE ///////////////////////////////////////

	const handleSortTableHeader = (headerName) => {
		setTrigger({
			data: headerName,
			trigger: trigger + 1,
		})
	}

	const [toggleAllNestedHandler, setToggleAllNestedHandler] = useState(false)

	const handleOnSubmit = (data) => {
		if (isUndefined(config.actions.edit)) return null
		const dataCP = cloneDeep(data)
		config.actions.edit.validate(dataCP, selectedData)
		setEditing(false)
	}

	const handleTableScrollTop = (e) => {
		tableRefWrapper.current.scrollLeft = e.target.scrollLeft
	}

	const handleScroll = (evt) => {
		const shiftKey = evt.nativeEvent.shiftKey

		if (!shiftKey) return

		if (Math.sign(evt.nativeEvent.deltaY) > 0) {
			tableRefTop.current.scrollLeft = tableRefWrapper.current.scrollLeft + 50
		} else {
			tableRefTop.current.scrollLeft = tableRefWrapper.current.scrollLeft - 50
		}
	}

	const [scrollBarRender, setScrollBarRender] = useState()

	const [widthTable, setWidthTable] = useState(0)

	// Sanitize the eval call
	const evalHandler = (_elem, obj1, obj2) => {
		eval(`_elem.${obj1} = ${obj2}`)
	}

	/////////////////////////////// USE EFFECT /////////////////////////////////////

	useEffect(() => {
		setToggleAllNestedHandler(config.nestedValues?.defaultVisible ? config.nestedValues?.defaultVisible : false)
	}, [])

	useEffect(() => {
		setTableData(cloneDeep(inputData))
	}, [inputData])

	useEffect(() => {
		setConfig(inputConfig)
	}, [inputConfig])

	useEffect(() => {
		// We need to check is a vertical scroll bar will be rendered
		// In this case, we slighty increased the width
		let scrollBarWidth = tableRefWrapper.current?.offsetWidth
		bodyWrapperRef.current?.offsetHeight > tableRefWrapper.current?.offsetHeight && (scrollBarWidth -= 20) // Or the precise size of scrollbar, if we can find it
		setScrollBarRender(
			<ScrollTopWrapper
				width={`${scrollBarWidth}px`}
				onScroll={handleTableScrollTop}
				isScrollable={tableRefWrapper.current?.offsetWidth < tableRef.current?.offsetWidth}
				ref={tableRefTop}
			>
				<ScrollTop width={`${widthTable}px`} />
			</ScrollTopWrapper>
		)
	}, [widthTable, dimensions.height])

	// When toggling all lines
	useEffect(() => {
		//setStateToggleNested(props.toggleAllNestedHandlerValue)
		let leTableData = cloneDeep(tableData)
		if (isEmpty(leTableData)) leTableData = cloneDeep(inputData)
		map(leTableData, (elem) => {
			evalHandler(elem, inputConfig.nestedValues?.toggleAllNestedPointer, toggleAllNestedHandler)
			//elem.toggleNested = toggleAllNestedHandler
		})
		setForceUnsort(true)
		setTableData(leTableData)
	}, [toggleAllNestedHandler])

	///////////////////////////////// RENDER ///////////////////////////////////////

	return (
		<TableWrapper>
			{scrollBarRender}

			<TableReverse
				onSubmit={handleSubmit(handleOnSubmit)}
				ref={tableRefWrapper}
				sizeheight={dimensions.height - 20}
				onScroll={handleScroll}
				autosize={true}
			>
				<TableContent ref={tableRef}>
					<TableNestedHeader
						data={tableData}
						columns={columns}
						sortTable={handleSortTableHeader}
						hasNestedValues={config.nestedValues}
						setToggleAllNestedHandler={() => setToggleAllNestedHandler(!toggleAllNestedHandler)}
						toggleAllNestedHandler={toggleAllNestedHandler}
						nestedTheme={config.nestedTheme}
					/>
					<TableNestedBody
						allData={tableData}
						editingData={selectedData}
						columns={columns}
						bodyWrapperRef={bodyWrapperRef}
						setWidthTable={setWidthTable}
						nestedValues={inputConfig.nestedValues}
						tableDataSetter={setTableData}
						setForceUnsort={setForceUnsort}
					/>
				</TableContent>
			</TableReverse>
		</TableWrapper>
	)
}

TableNested.propTypes = {
	inputColumns: PropTypes.array.isRequired,
	inputData: PropTypes.array,
	inputConfig: PropTypes.object.isRequired,
}
TableNested.defaultPropTypes = {
	inputColumns: [],
	inputData: [],
	inputConfig: {},
}

export default TableNested
